"use client";

import React, { useState, useMemo, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { SlidersHorizontal, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice, type EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

// Language hook
const useWvcLanguage = () => {
  const [lang, setLang] = useState("en");
  useEffect(() => {
    const handleLang = () => {
      const htmlLang = document.documentElement.lang || "en";
      const normalized = htmlLang.toLowerCase();
      if (normalized.startsWith("zh")) setLang("zh");else if (normalized.startsWith("fr")) setLang("fr");else if (normalized.startsWith("es")) setLang("es");else if (normalized.startsWith("de")) setLang("de");else setLang("en");
    };
    handleLang();
    window.addEventListener("languagechange", handleLang);
    const observer = new MutationObserver(handleLang);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"]
    });
    return () => {
      window.removeEventListener("languagechange", handleLang);
      observer.disconnect();
    };
  }, []);
  return lang as "en" | "zh" | "fr" | "es" | "de";
};
const translations = {
  en: {
    allGarments: "All Garments",
    dressesSets: "Dresses & Sets",
    outerwearAccessories: "Outerwear & Accessories",
    topsBottoms: "Tops & Bottoms",
    showing: "Showing",
    results: "Results",
    resetFilters: "Reset filters",
    filters: "Filters",
    refineCollection: "Refine Collection",
    filterDescription: "Filter garments by curated collections and categories.",
    category: "Category",
    categories: "Categories",
    apply: "Apply Filters",
    tagline: "Ethically sourced European yarns, bespoke draping, and zero fast-fashion excess.",
    noMatches: "No garments match this selection",
    tryAdjusting: "Try adjusting or clearing your category filters.",
    clearAll: "Clear all filters",
    newIn: "New Arrivals",
    bestSeller: "Best Seller",
    sale: "Sale",
    sort: "Sort:",
    featured: "Featured Curations",
    newest: "Newest Arrivals",
    priceLow: "Price: Low to High",
    priceHigh: "Price: High to Low",
    viewGarment: "View Garment",
    loadMore: "Load More",
    loading: "Loading..."
  },
  zh: {
    allGarments: "所有服装",
    dressesSets: "连衣裙与套装",
    outerwearAccessories: "外套与配饰",
    topsBottoms: "上衣与下装",
    showing: "显示",
    results: "结果",
    resetFilters: "重置过滤器",
    filters: "过滤器",
    refineCollection: "完善系列",
    filterDescription: "按精选系列和类别过滤服装。",
    category: "类别",
    categories: "类别",
    apply: "应用过滤器",
    tagline: "从伦理上采购的欧洲纱线、定制褶皱和零快速时尚过量。",
    noMatches: "没有服装符合此选择",
    tryAdjusting: "尝试调整或清除您的类别过滤器。",
    clearAll: "清除所有过滤器",
    newIn: "新进货",
    bestSeller: "畅销",
    sale: "特惠",
    sort: "排序:",
    featured: "精选策划",
    newest: "最新到货",
    priceLow: "价格：低到高",
    priceHigh: "价格：高到低",
    viewGarment: "查看服装",
    loadMore: "加载更多",
    loading: "加载中..."
  },
  fr: {
    allGarments: "Tous les Vêtements",
    dressesSets: "Robes et Ensembles",
    outerwearAccessories: "Vêtements d'Extérieur et Accessoires",
    topsBottoms: "Hauts et Bas",
    showing: "Affichage",
    results: "Résultats",
    resetFilters: "Réinitialiser les Filtres",
    filters: "Filtres",
    refineCollection: "Affiner la Collection",
    filterDescription: "Filtrez les vêtements par collections sélectionnées et catégories.",
    category: "Catégorie",
    categories: "Catégories",
    apply: "Appliquer les Filtres",
    tagline: "Fils européens d'approvisionnement éthique, drapé sur mesure et zéro excédent de fast-fashion.",
    noMatches: "Aucun vêtement ne correspond à cette sélection",
    tryAdjusting: "Essayez d'ajuster ou d'effacer vos filtres de catégorie.",
    clearAll: "Effacer tous les Filtres",
    newIn: "Nouveautés",
    bestSeller: "Meilleure Vente",
    sale: "Solde",
    sort: "Trier:",
    featured: "Curés Présentés",
    newest: "Dernières Arrivées",
    priceLow: "Prix: Bas en Haut",
    priceHigh: "Prix: Haut en Bas",
    viewGarment: "Afficher Vêtement",
    loadMore: "Charger Plus",
    loading: "Chargement..."
  },
  es: {
    allGarments: "Todas las Prendas",
    dressesSets: "Vestidos y Conjuntos",
    outerwearAccessories: "Prendas de Abrigo y Accesorios",
    topsBottoms: "Tops y Pantalones",
    showing: "Mostrando",
    results: "Resultados",
    resetFilters: "Restablecer Filtros",
    filters: "Filtros",
    refineCollection: "Refinar Colección",
    filterDescription: "Filtra prendas por colecciones seleccionadas y categorías.",
    category: "Categoría",
    categories: "Categorías",
    apply: "Aplicar Filtros",
    tagline: "Hilos europeos de origen ético, drapeos personalizados y cero exceso de moda rápida.",
    noMatches: "Ninguna prenda coincide con esta selección",
    tryAdjusting: "Intenta ajustar o borrar tus filtros de categoría.",
    clearAll: "Borrar Todos los Filtros",
    newIn: "Novedades",
    bestSeller: "Más Vendido",
    sale: "Rebajas",
    sort: "Clasificar:",
    featured: "Curaciones Destacadas",
    newest: "Últimas Llegadas",
    priceLow: "Precio: Bajo a Alto",
    priceHigh: "Precio: Alto a Bajo",
    viewGarment: "Ver Prenda",
    loadMore: "Cargar Más",
    loading: "Cargando..."
  },
  de: {
    allGarments: "Alle Kleidungsstücke",
    dressesSets: "Kleider & Sets",
    outerwearAccessories: "Oberbekleidung & Accessoires",
    topsBottoms: "Oberteile & Hosen",
    showing: "Anzeigen",
    results: "Ergebnisse",
    resetFilters: "Filter Zurücksetzen",
    filters: "Filter",
    refineCollection: "Kollektion verfeinern",
    filterDescription: "Filtern Sie Kleidung nach kuratierten Kollektionen und Kategorien.",
    category: "Kategorie",
    categories: "Kategorien",
    apply: "Filter Anwenden",
    tagline: "Ethisch bezogene europäische Garne, maßgeschneiderte Drapierungen und kein Fast-Fashion-Überschuss.",
    noMatches: "Keine Kleidung passt zu dieser Auswahl",
    tryAdjusting: "Versuchen Sie, Ihre Kategoriefilter anzupassen oder zu löschen.",
    clearAll: "Alle Filter Löschen",
    newIn: "Neuheiten",
    bestSeller: "Bestseller",
    sale: "Angebot",
    sort: "Sortieren:",
    featured: "Empfohlene Kurationen",
    newest: "Neueste Ankünfte",
    priceLow: "Preis: Niedrig bis Hoch",
    priceHigh: "Preis: Hoch bis Niedrig",
    viewGarment: "Kleidung Anzeigen",
    loadMore: "Mehr Laden",
    loading: "Laden..."
  }
};
const categories = [{
  id: null,
  name: "All Garments",
  slug: "all",
  key: "allGarments" as const
}, {
  id: 16,
  name: "Dresses & Sets",
  slug: "dresses-sets",
  key: "dressesSets" as const
}, {
  id: 18,
  name: "Outerwear & Accessories",
  slug: "outerwear-accessories",
  key: "outerwearAccessories" as const
}, {
  id: 17,
  name: "Tops & Bottoms",
  slug: "tops-bottoms",
  key: "topsBottoms" as const
}];
function getProductBadge(product: EcommerceProduct) {
  const tags = product.tags || [];
  for (const tag of tags) {
    const tagName = tag.name || "";
    if (tagName.toLowerCase().includes("new arrival")) {
      return {
        label: "New Arrivals",
        type: "new"
      };
    }
    if (tagName.toLowerCase().includes("best seller")) {
      return {
        label: "Best Seller",
        type: "accent"
      };
    }
    if (tagName.toLowerCase().includes("sale")) {
      return {
        label: "Sale",
        type: "sale"
      };
    }
  }
  return null;
}
interface ProductConsumerProps {
  t: typeof translations.en;
  selectedCategory: number | null;
  setSelectedCategory: (id: number | null) => void;
  sortBy: string;
  setSortBy: (val: string) => void;
  mobileFilterOpen: boolean;
  setMobileFilterOpen: (open: boolean) => void;
  clearAllFilters: () => void;
}
function ProductGridConsumer({
  t,
  selectedCategory,
  setSelectedCategory,
  sortBy,
  setSortBy,
  mobileFilterOpen,
  setMobileFilterOpen,
  clearAllFilters
}: ProductConsumerProps) {
  const {
    products,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching,
    total
  } = useEcommerceProducts();
  const activeFiltersCount = selectedCategory !== null ? 1 : 0;
  return <>
      {/* Top Control Bar: Total Count & Mobile Filter Toggle */}
      <div className="mb-8 flex flex-col gap-4 border-b border-border pb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="font-sans text-xs tracking-widest text-muted-foreground uppercase">
            {t.showing} {total ?? (loading ? 0 : products.length)} {t.results}
          </span>
          {activeFiltersCount > 0 && <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-7 text-xs text-primary underline underline-offset-4 hover:bg-transparent">
              {t.resetFilters} ({activeFiltersCount})
            </Button>}
        </div>

        <div className="flex items-center gap-3">
          {/* Mobile Filter Drawer Trigger */}
          <Sheet open={mobileFilterOpen} onOpenChange={setMobileFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2 rounded-none border-border font-sans text-xs uppercase tracking-wider md:hidden">
                <SlidersHorizontal className="size-3.5" />
                {t.filters} {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ""}
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full max-w-xs bg-background p-6 text-foreground overflow-y-auto">
              <SheetHeader className="mb-6 border-b border-border pb-4 text-left">
                <SheetTitle className="font-serif text-xl font-normal">
                  {t.refineCollection}
                </SheetTitle>
                <SheetDescription className="text-xs text-muted-foreground">
                  {t.filterDescription}
                </SheetDescription>
              </SheetHeader>

              {/* Mobile Filter Body */}
              <div className="flex flex-col gap-8">
                {/* Category Filter */}
                <div>
                  <h3 className="mb-3 font-sans text-xs font-semibold tracking-wider text-foreground uppercase">
                    {t.category}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {categories.map((cat, i) => <Button key={cat.slug} data-index={i} variant="ghost" onClick={() => setSelectedCategory(cat.id)} className={`justify-between rounded-none px-2 py-1.5 text-left text-sm ${selectedCategory === cat.id ? "bg-muted font-medium text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                        <span>{t[cat.key] || cat.name}</span>
                      </Button>)}
                  </div>
                </div>

                <div className="mt-4 flex gap-3">
                  <Button variant="default" onClick={() => setMobileFilterOpen(false)} className="w-full rounded-none bg-primary text-primary-foreground py-5 text-xs tracking-widest uppercase">
                    {t.apply}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Sort Selector */}
          <div className="flex items-center gap-2">
            <span className="hidden font-sans text-xs tracking-wider text-muted-foreground uppercase sm:inline-block">
              {t.sort}
            </span>
            <div className="relative">
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="h-9 appearance-none rounded-none border border-border bg-background px-3 pr-8 text-xs tracking-wider text-foreground uppercase focus:border-foreground focus:outline-none cursor-pointer">
                <option value="featured">{t.featured}</option>
                <option value="newest">{t.newest}</option>
                <option value="price-low">{t.priceLow}</option>
                <option value="price-high">{t.priceHigh}</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout: Sidebar + Products Grid */}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[240px_1fr]">
        {/* Desktop Left Sticky Sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-28 flex flex-col gap-8 pr-4">
            {/* Category Filter */}
            <div>
              <div className="mb-4 flex items-center justify-between border-b border-border pb-2">
                <h3 className="font-sans text-xs font-semibold tracking-widest text-foreground uppercase">
                  {t.categories}
                </h3>
              </div>
              <div className="flex flex-col gap-1">
                {categories.map((cat, i) => {
                const isActive = selectedCategory === cat.id;
                return <Button key={cat.slug} data-index={i} variant="ghost" onClick={() => setSelectedCategory(cat.id)} className={`group flex h-8 w-full items-center justify-between rounded-none px-2 text-left text-sm transition-colors ${isActive ? "bg-card font-medium text-foreground" : "text-muted-foreground hover:bg-card/50 hover:text-foreground"}`}>
                      <span className="truncate">{t[cat.key] || cat.name}</span>
                    </Button>;
              })}
              </div>
            </div>

            {/* Editorial Fabric Care Notice */}
            <div className="border-t border-border pt-6">
              <p className="font-serif text-sm italic text-muted-foreground">
                {t.tagline}
              </p>
            </div>
          </div>
        </aside>

        {/* Product Grid Area */}
        <div className="flex flex-col">
          {loading && products.length === 0 ? <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-3">
                  <div className="aspect-[3/4] w-full bg-card animate-pulse" />
                  <div className="h-4 w-3/4 bg-muted animate-pulse" />
                  <div className="h-3 w-1/3 bg-muted animate-pulse" />
                </div>)}
            </div> : error && products.length === 0 ? <div className="flex min-h-[360px] flex-col items-center justify-center border border-dashed border-border p-12 text-center">
              <p className="font-serif text-2xl text-foreground">{t.noMatches}</p>
              <p className="mt-2 text-sm text-muted-foreground">{t.tryAdjusting}</p>
              <Button variant="outline" onClick={clearAllFilters} className="mt-6 rounded-none border-foreground text-xs uppercase tracking-widest">
                {t.clearAll}
              </Button>
            </div> : products.length === 0 ? <div className="flex min-h-[360px] flex-col items-center justify-center border border-dashed border-border p-12 text-center">
              <p className="font-serif text-2xl text-foreground">{t.noMatches}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                {t.tryAdjusting}
              </p>
              <Button variant="outline" onClick={clearAllFilters} className="mt-6 rounded-none border-foreground text-xs uppercase tracking-widest">
                {t.clearAll}
              </Button>
            </div> : <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-3 xl:grid-cols-4">
              {products.map((product, i) => {
            const badge = getProductBadge(product);
            const categoryName = product.categories?.[0]?.name || "";
            return <div key={product.id} data-index={i} className="group relative flex flex-col">
                    {/* 3:4 Aspect Ratio Image Box */}
                    <div className="relative aspect-[3/4] w-full overflow-hidden bg-card">
                      <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-105" />

                      {/* Status Badges */}
                      {badge && <div className="absolute top-2.5 left-2.5 z-10">
                          <span className={`px-2 py-0.5 text-[10px] font-semibold tracking-wider uppercase ${badge.type === "new" ? "bg-secondary text-secondary-foreground" : badge.type === "accent" ? "bg-accent text-accent-foreground" : "bg-primary text-primary-foreground"}`}>
                            {badge.label}
                          </span>
                        </div>}

                      {/* Hover Slide-Up Quick Link */}
                      <div className="absolute inset-x-0 bottom-0 translate-y-full p-2.5 transition-transform duration-300 ease-out group-hover:translate-y-0">
                        <Button asChild className="w-full rounded-none bg-background/95 text-foreground py-2 text-[11px] font-medium tracking-widest uppercase shadow-sm backdrop-blur hover:bg-primary hover:text-primary-foreground transition-colors">
                          <Link to={product.link || "/ShopPage"}>{t.viewGarment}</Link>
                        </Button>
                      </div>
                    </div>

                    {/* Metadata & Details */}
                    <div className="mt-3 flex flex-col gap-1 border-t border-border pt-2.5">
                      <div className="flex items-baseline justify-between gap-2">
                        <h4 className="font-sans text-xs md:text-sm font-medium text-foreground tracking-normal truncate">
                          {product.title}
                        </h4>
                        <ProductPrice product={product} className="font-sans text-xs md:text-sm font-semibold text-foreground shrink-0" />
                      </div>

                      {/* Category metadata */}
                      {categoryName && <div className="mt-1 flex items-center justify-between">
                          <span className="font-sans text-xs text-muted-foreground truncate">
                            {categoryName}
                          </span>
                        </div>}
                    </div>
                  </div>;
          })}
            </div>}

          {/* Load More Pagination */}
          {hasNext && <div className="mt-16 flex justify-center border-t border-border pt-8">
              <Button variant="outline" onClick={() => loadMore()} disabled={isRefetching} className="rounded-none border-foreground px-8 py-5 text-xs font-medium uppercase tracking-widest text-foreground hover:bg-card transition-colors">
                {isRefetching ? t.loading : t.loadMore}
              </Button>
            </div>}
        </div>
      </div>
    </>;
}
export default function Listings() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("featured");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const clearAllFilters = () => {
    setSelectedCategory(null);
  };
  const wp_query = useMemo(() => {
    const params: Record<string, any> = {
      per_page: 72
    };
    if (selectedCategory !== null) {
      params.product_cat = selectedCategory;
    }
    if (sortBy === "newest") {
      params.orderby = "date";
      params.order = "desc";
    } else if (sortBy === "price-low") {
      params.orderby = "price";
      params.order = "asc";
    } else if (sortBy === "price-high") {
      params.orderby = "price";
      params.order = "desc";
    }
    return new WP_Query(params);
  }, [selectedCategory, sortBy]);
  return <section id="product-listings" data-nav="light" className="bg-background text-foreground py-12 md:py-20">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
        <EcommerceProductsProvider wp_query={wp_query}>
          <ProductGridConsumer t={t} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} sortBy={sortBy} setSortBy={setSortBy} mobileFilterOpen={mobileFilterOpen} setMobileFilterOpen={setMobileFilterOpen} clearAllFilters={clearAllFilters} />
        </EcommerceProductsProvider>
      </div>
    </section>;
}